import React from 'react';
import { StylesConfig } from 'react-select';
import { Link } from 'react-router-dom';
import { PATHS } from 'src/common/constants';
import Callout from 'src/components/InformationCallout/Callout';
import { ModernSelect, ModernSelectOption } from 'src/components/ModernSelect';
import { CALCOM_SELECT_STYLES } from 'src/pages/Calendar/CalcomIntegration/calcomSelectStyles';
import {
  IUsePropertyBookingLinks,
  usePropertyBookingLinks,
} from 'src/pages/Calendar/CalcomIntegration/CustomHooks/usePropertyBookingLinks/usePropertyBookingLinks';
import {
  IUseCalcomEventTypes,
  useCalcomEventTypes,
} from 'src/pages/Calendar/CalcomIntegration/CustomHooks/useCalcomEventTypes';
import {
  ICalcomEventType,
  IPropertyBookingLink,
  JuniperEventType,
} from 'src/pages/Calendar/CalcomIntegration/types';
import './calcom-booking-link-picker.scss';

interface CalcomBookingLinkPickerProps {
  propertyId: string;
  selectedValue: string | null;
  onChange: (bookingLinkId: string | null) => void;
  kind?: JuniperEventType;
  required?: boolean;
}

interface BookingLinkOption extends ModernSelectOption {
  hasValidEventType: boolean;
  isDisabled: boolean;
}

// Shared Cal.com dropdown styling + this picker's disabled-option treatment (links with a missing
// event type can't be chosen).
const BOOKING_LINK_PICKER_STYLES: StylesConfig<BookingLinkOption, false> = {
  ...(CALCOM_SELECT_STYLES as StylesConfig<BookingLinkOption, false>),
  option: (base, state) => {
    if (!state.isDisabled) {
      return base;
    }

    return {
      ...base,
      cursor: 'not-allowed',
      backgroundColor: '#f9fafb',
    };
  },
};

interface BookingLinkOptionLabelProps {
  option: BookingLinkOption;
}

const BookingLinkOptionLabel = ({ option }: BookingLinkOptionLabelProps) => {
  if (option.hasValidEventType) {
    return <>{option.label}</>;
  }

  return (
    <div className="booking-link-option-invalid">
      <span className="booking-link-option-invalid-label">{option.label}</span>
      <span className="booking-link-option-invalid-note">
        No valid event type — fix this link in the Bookings tab.
      </span>
    </div>
  );
};

const getBookingLinkKindLabel = (kind: JuniperEventType): string =>
  kind === JuniperEventType.KeyPickup ? 'key-pickup' : 'walkthrough';

const getBookingLinkKindLabelCapitalized = (kind: JuniperEventType): string =>
  kind === JuniperEventType.KeyPickup ? 'Key-pickup' : 'Walkthrough';

const formatLinkLabel = (link: IPropertyBookingLink): string => {
  const owner: string = [link.ownerFirstName, link.ownerLastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (!owner) {
    return link.label;
  }

  return `${link.label} — ${owner}`;
};

const CalcomBookingLinkPicker = ({
  propertyId,
  selectedValue,
  onChange,
  kind = JuniperEventType.Walkthrough,
  required = false,
}: CalcomBookingLinkPickerProps) => {
  const { links, isLoading, error, hasNoLinks }: IUsePropertyBookingLinks =
    usePropertyBookingLinks(propertyId, kind);

  const {
    eventTypes,
    isLoading: isLoadingEventTypes,
    error: eventTypesError,
  }: IUseCalcomEventTypes = useCalcomEventTypes();

  const bookingLinkKindLabel: string = getBookingLinkKindLabel(kind);

  if (error) {
    return (
      <Callout
        variant="error"
        title="Couldn't load booking links"
        subtitle={error}
      />
    );
  }

  if (hasNoLinks) {
    return (
      <Callout
        variant="error"
        title={`No ${bookingLinkKindLabel} booking links yet`}
        subtitle={`Recipients can't self-schedule until this property has a valid ${bookingLinkKindLabel} booking link that belongs to you. Create one in your Cal.com settings, then come back and refresh this page.`}
      >
        <Link
          to={`${PATHS.CALENDAR_CALCOM_BOOKINGS}/${propertyId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="calcom-booking-link-picker-cta"
        >
          Create a booking link that belongs to your Cal.com account
        </Link>
      </Callout>
    );
  }

  const eventTypesLoaded: boolean = !isLoadingEventTypes;

  const eventTypesResolved: boolean =
    eventTypesLoaded && eventTypesError === null;

  const validEventTypeIds: Set<number> = new Set(
    eventTypes.map((eventType: ICalcomEventType) => eventType.id)
  );

  const toBookingLinkOption = (
    link: IPropertyBookingLink
  ): BookingLinkOption => {
    const eventTypeExists: boolean = validEventTypeIds.has(
      link.calcomEventTypeId
    );

    // Until event types load we can't tell one is missing, so assume the link is valid.
    const hasValidEventType: boolean = eventTypesResolved
      ? eventTypeExists
      : true;

    return {
      value: link.id,
      label: formatLinkLabel(link),
      hasValidEventType,
      isDisabled: !hasValidEventType,
    };
  };

  const options: BookingLinkOption[] = links.map((link: IPropertyBookingLink) =>
    toBookingLinkOption(link)
  );

  const isCurrentlySelected = (option: BookingLinkOption): boolean =>
    option.value === selectedValue;

  const handleChange = (option: BookingLinkOption | null): void => {
    onChange(option ? option.value : null);
  };

  const matchedOption: BookingLinkOption | undefined = options.find(
    (option: BookingLinkOption) => isCurrentlySelected(option)
  );

  const selectedOption: BookingLinkOption | null = matchedOption ?? null;

  return (
    <div className="calcom-booking-link-picker">
      <span
        className={`calcom-booking-link-picker-label${
          required ? ' calcom-booking-link-picker-label-required' : ''
        }`}
      >
        {getBookingLinkKindLabelCapitalized(kind)} booking link
      </span>
      <ModernSelect
        size="regular"
        options={options}
        value={selectedOption}
        onChange={(option: BookingLinkOption | null) => handleChange(option)}
        isLoading={isLoading || isLoadingEventTypes}
        placeholder="Select a booking link…"
        styles={BOOKING_LINK_PICKER_STYLES}
        formatOptionLabel={(option: BookingLinkOption) => (
          <BookingLinkOptionLabel option={option} />
        )}
      />
    </div>
  );
};

export default CalcomBookingLinkPicker;
